// Instance
import MainAPI from "@/app/apis/main/main-api.js";

// Endpoints
import { API, ENDPOINTS } from "@/app/apis/main/_connection/connection-api.js";

const queryString = require("querystring")

class breedingManagementApi extends MainAPI {

	async getBreederDamAndSireList(queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/list?${queryString.stringify(queryParams)}`)
	}

	/**
	* Fetches the details of a Dam/Sire by sending a GET request.
	*
	* @async
	* @function getDamAndSireList
	* @param {number} damSireId - The ID of the Dam/Sire to retrieve.
	* @returns {Promise<Object>} The response from the API containing the Dam/Sire details.
	* @throws {Error} If the API request fails.
	*/
	async getDamSireDetails(breederSysId, damSireId) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/${breederSysId}/${damSireId}`);
	}


	/**
	 * Updates an existing Dam/Sire by sending a PUT request with form data.
	 *
	 * @async
	 * @function updateAdminDamSire
	 * @param {number} damSireId - The ID of the Dam/Sire to be updated.
	 * @param {Object} damSireDetails - The updated details of the Dam/Sire.
	 * @param {Object} damSireDetails.formData - The form data for the Dam/Sire.
	 * @param {File} [damSireDetails.dogImageUpload] - The file for the dog image (optional).
	 * @returns {Promise<Object>} The response from the API.
	 * @throws {Error} If the API request fails.
	 */
	async updateAdminDamSire(breederSysId, damSireId, damSireDetails) {
		let newFormData = new FormData();

		const { formData, dogImageUpload } = damSireDetails;

		newFormData.append("formData", JSON.stringify(formData));

		if (dogImageUpload) {
			newFormData.append("dogImageUpload", dogImageUpload);
		}

		return API.put(`${ENDPOINTS.PUT_DAM_SIRE}/${breederSysId}/${damSireId}`, newFormData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	}

	async getMatingList(queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/mating-list?${queryString.stringify(queryParams)}`)
	}

	async deleteMatingDetails(breederSysId, matingId) {
		return API.delete(`${ENDPOINTS.GET_DAM_SIRE}/${breederSysId}/${matingId}/mating-details`)
	}

	async getLitterManagementList(queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/litter-management-list?${queryString.stringify(queryParams)}`)
	}

	/**
	* Retrieves a list of breeders based on the specified gender from the API.
	* 
	* @param {("Dam" | "Sire")} gender - The gender of the breeders to retrieve. It can be either "Dam" or "Sire".
	* @returns {Promise<any>} A promise that resolves to the API response containing the list of breeders.
	*/
	async getBreederDamAndSireListByGender(breederSysId, gender) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/${breederSysId}/${gender}/list`);
	}

	/**
	 * Fetches the mating details by id by sending a GET request.
	 *
	 * @async
	 * @function getDamSireDetails
	 * @param {number} matingId - The ID of the Dam/Sire to retrieve.
	 * @returns {Promise<Object>} The response from the API containing the Dam/Sire details.
	 * @throws {Error} If the API request fails.
	 */
	async getDamAndSireMatingDetails(breederSysId, matingId) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/mating/${breederSysId}/${matingId}`)
	}

	async getDamDiaryDetails(damId, breederSysId) {
		return API.get(`${ENDPOINTS.GET_OVULATION_GESTATION}/history/${breederSysId}/${damId}`)
	}

	/**
	 * Updates mating details for a specific mating ID using the API.
	 * 
	 * @param {number} matingId - The ID of the mating to be updated.
	 * @param {Object} matingDetails - The updated details of the mating.
	 * @param {number | null} matingDetails.damId - The ID of the dam. `null` if not applicable.
	 * @param {number | null} matingDetails.sireId - The ID of the sire. `null` if not applicable.
	 * @param {string} matingDetails.note - Additional notes about the mating.
	 * @returns {Promise<any>} A promise that resolves to the API response after updating the mating details.
	 */
	async updateDamAndSireMatingDetails(breederSysId, matingId, matingDetails) {
		return API.put(`${ENDPOINTS.PUT_DAM_SIRE}/mating/${breederSysId}/${matingId}`, matingDetails);
	}

	
	async getLitterPuppyList(breederSysId, matingId, queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/${breederSysId}/${matingId}/litter-puppy-list?${queryString.stringify(queryParams)}`)
	}

		
	async deleteLitterPuppy(breederSysId, litterPuppyId) {
		return API.delete(`${ENDPOINTS.GET_DAM_SIRE}/${breederSysId}/${litterPuppyId}/delete-litter-puppy`)
	}

	// *********************************** DOCUMENTS ****************************************************
    /**
     * Adds a new document by sending a POST request with form data.
     *
     * @async
     * @function uploadDamSireDocument
     * @param {Object} uploadDetails - The details of the Dam/Sire to be added.
     * @param {Object} uploadDetails.formData - The form data for the Dam/Sire.
     * @param {File} [uploadDetails.document] - The file for the document (required).
     * @returns {Promise<Object>} The response from the API.
     * @throws {Error} If the API request fails.
    */
    async uploadDamSireDocument(breederSysId, uploadDetails) {
        let newFormData = new FormData();
        
        const { formData, document } = uploadDetails;
        
        newFormData.append("formData", JSON.stringify(formData));
        newFormData.append("document", document);
        
        return API.post(`${ENDPOINTS.POST_DAM_SIRE_DOCUMENT}/${breederSysId}`, newFormData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }
    
    /**
     * Fetches the list of Dam/Sire documents based on the provided query parameters.
     *
     * @async
     * @function getDamSireDocumentsList
     * @param {Object} queryParams - The query parameters used to filter or paginate the document list.
     * @param {string} [queryParams.damSireId] - The ID of the Dam/Sire to filter documents.
     * @param {string} [queryParams.sortBy] - The field to sort by (e.g., 'createdAt').
     * @param {string} [queryParams.filterBy] - The field to sort by (e.g., 'fileName', 'fileType').
     * @param {string} [queryParams.order] - The sort order ('asc' or 'desc').
     * @param {number} [queryParams.page] - The page number for pagination.
     * @param {number} [queryParams.limit] - The number of documents to retrieve per page.
     * @returns {Promise<Object>} The response from the API containing the list of documents.
     * @throws {Error} If the API request fails.
     */
    async getDamSireDocumentsList(damSireId, queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/documents/${damSireId}?${queryString.stringify(queryParams)}`)
    }


    /**
     * Deletes a dam/sire document from the server.
     * 
     * @async
     * @function deleteDamSireDocument
     * @param {string} documentId - The ID of the document to delete.
     * @param {Object} documentDetails - The details of the document being deleted.
     * @param {string} documentDetails.damSireId - The ID of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the delete operation.
     * @throws {Error} If the API request fails.
     */
    async deleteDamSireDocument(documentId, documentDetails) {
		return API.delete(`${ENDPOINTS.DELETE_DAM_SIRE_DOCUMENT}/${documentId}`, { data: documentDetails });
    }

    /**
     * Updates a dam/sire document from the server.
     * 
     * @async
     * @function updateDamSireDocument
     * @param {string} documentId - The ID of the document to update.
     * @param {Object} documentDetails - The details of the document being updated.
     * @param {string} documentDetails.description - The descsription of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the update operation.
     * @throws {Error} If the API request fails.
     */
    async updateDamSireDocument(documentId, documentDetails) {
        return API.put(`${ENDPOINTS.PUT_DAM_SIRE_DOCUMENT}/${documentId}`, documentDetails);
    }
	/**
	 * Fetches ovulation and gestation details for a specific dam.
	 *
	 * @async
	 * @function getDamOvulationAndGestationDetails
	 * @param {string} damId - The ID of the dam.
	 * @param {string} breederSysId - The ID of the breeder system.
	 * @returns {Promise<Object>} A promise that resolves to the ovulation and gestation details.
	 * @throws {Error} Throws an error if the request fails or the response is not as expected.
	 */
	async getDamOvulationAndGestationDetails(damId, breederSysId) {
		return API.get(`${ENDPOINTS.GET_OVULATION_GESTATION}/${breederSysId}/${damId}/details`);
	}

	/**
	 * Saves ovulation and gestation details for a dam.
	 *
	 * @async
	 * @function saveOvulationAndGestation
	 * @param {Object} details - The ovulation and gestation details to save.
	 * @param {number} details.damId - The ID of the dam.
	 * @param {number} details.breederSysId - The ID of the breeder.
	 * @param {string} details.matingDate - The mating date of the dam (in ISO 8601 format).
	 * @param {string} details.ovulationStartDate - The ovulation start date of the dam (in ISO 8601 format).
	 * @returns {Promise<Object>} A promise that resolves to the response from the API.
	 */
	async saveOvulationAndGestation(details) {
		return API.post(`${ENDPOINTS.GET_OVULATION_GESTATION}/details`, details);
	}

	/**
	 * Fetches the details of the dam and sire litter based on the provided breeder system ID and mating ID.
	 *
	 * @async
	 * @function getDamAndSireLitterDetails
	 * @param {number} breederSysId - The unique identifier for the breeder.
	 * @param {number} litterId - The unique identifier for the litter puppy.
	 * @returns {Promise<Object>} A promise that resolves to the litter details of the dam and sire.
	 * @throws {Error} Throws an error if the API request fails or if the parameters are invalid.
	 */
	async getDamAndSireLitterDetails(breederSysId, litterId) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/litter/${breederSysId}/${litterId}/details`);
	}

	async saveLitterPuppyDetails(breederSysId, litterId, details) {
		return API.put(`${ENDPOINTS.GET_DAM_SIRE}/litter/${breederSysId}/${litterId}/details`, details);
	}
}

export default new breedingManagementApi();
